import { useEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { ThunkDispatch } from "@reduxjs/toolkit"
import { Modal } from "flowbite-react"
import { Slider } from 'primereact/slider'
import { imageEditingActions, ProcessingHistory } from '../../reducers/imageEditing'
import { getRandId } from '../../libs'
import {
  RedoIcon,
  SquareOutlineIcon,
  UndoIcon
} from '../SVGIcons'
import ImageEditingDrawing from './ImageEditingDrawing'

/**
 * 画像編集の描画ポップアップ
 * マウスを離した時にモザイクが即座に適用される
 * Undo/Redoでモザイク履歴を操作できる
 */
const ImageEditingDrawingPopup = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>()

  const {
    showImageEditingDrawing,
    imageEditingBrushSize,
    imageEditingDrawingIsReEdit,
    imageEditingMosaicResultUrl,
    imageEditingMosaicHistoryStack,
    imageEditingMosaicHistoryIndex,
    selectedImage,
  } = useSelector((state: any) => state.imageEditing)

  // 前回のポップアップ表示状態を追跡
  const prevShowRef = useRef(false)

  // ポップアップが開いた時の初期化処理
  useEffect(() => {
    // falseからtrueになった時のみ初期化
    if (showImageEditingDrawing && !prevShowRef.current) {
      // 再編集フラグがtrueの場合はクリアしない
      if (imageEditingDrawingIsReEdit) {
        dispatch(imageEditingActions.setImageEditingDrawingIsReEdit(false))
      } else {
        // 描画状態とモザイク履歴をリセット
        dispatch(imageEditingActions.clearImageEditingDrawingLines())
        dispatch(imageEditingActions.clearMosaicHistory())
        dispatch(imageEditingActions.setImageEditingMosaicResultUrl(null))
      }
    }
    prevShowRef.current = showImageEditingDrawing
  }, [showImageEditingDrawing, imageEditingDrawingIsReEdit, dispatch])

  const closePopup = () => {
    dispatch(imageEditingActions.setShowImageEditingDrawing(false))
    // 描画状態とモザイク履歴をクリア
    dispatch(imageEditingActions.clearImageEditingDrawingLines())
    dispatch(imageEditingActions.clearMosaicHistory())
    dispatch(imageEditingActions.setImageEditingMosaicResultUrl(null))
  }

  const handleConfirm = () => {
    // モザイク結果がある場合は履歴に追加
    if (imageEditingMosaicResultUrl && selectedImage) {
      const historyEntry: ProcessingHistory = {
        id: getRandId().toString(),
        originalImageId: selectedImage.id,
        processedImageUrl: imageEditingMosaicResultUrl,
        processingType: 'manualMosaic',
        timestamp: Date.now(),
        thumbnailUrl: imageEditingMosaicResultUrl,
      }

      dispatch(imageEditingActions.addHistoryEntry({
        imageId: selectedImage.id,
        history: historyEntry,
      }))
    }

    // ポップアップを閉じる
    dispatch(imageEditingActions.setShowImageEditingDrawing(false))
    // 描画状態とモザイク履歴をクリア
    dispatch(imageEditingActions.clearImageEditingDrawingLines())
    dispatch(imageEditingActions.clearMosaicHistory())
    dispatch(imageEditingActions.setImageEditingMosaicResultUrl(null))
  }

  // モザイク履歴のUndo
  const handleUndo = () => {
    dispatch(imageEditingActions.undoMosaicHistory())
  }

  // モザイク履歴のRedo
  const handleRedo = () => {
    dispatch(imageEditingActions.redoMosaicHistory())
  }

  // 全てクリア（元画像に戻す）
  const handleClearAll = () => {
    dispatch(imageEditingActions.clearMosaicHistory())
    dispatch(imageEditingActions.setImageEditingMosaicResultUrl(null))
  }

  // モザイク履歴のUndo/Redo可否
  // index 0 は元画像なので、index > 0 の場合のみUndoできる
  const canUndo = imageEditingMosaicHistoryIndex > 0
  const canRedo = imageEditingMosaicHistoryIndex < imageEditingMosaicHistoryStack.length - 1
  // モザイクが適用されている（履歴が2つ以上＝元画像＋モザイク結果がある）場合に確定可能
  const hasMosaicApplied = imageEditingMosaicHistoryStack.length > 1
  const canConfirm = hasMosaicApplied

  const renderBrushShape = (size: number) => {
    return <SquareOutlineIcon style={{ stroke: "#595959", strokeWidth: '2px', width: `${size}px`, height: `${size}px` }} />
  }

  return (
    <>
      <Modal show={showImageEditingDrawing} onClose={closePopup} size="xl">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex items-end max-h-full justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-xl">
              {/* 1行目：ヘッダー（タイトル + キャンセル/実行ボタン） */}
              <div className="py-2 px-4 bg-orange-500 text-white flex items-start justify-between">
                <div>
                  <p>• 編集したい部分を塗りつぶしてください。</p>
                  <p>• 塗りつぶした部分に対してモザイクが適用されます。</p>
                </div>
                <button
                  onClick={closePopup}
                  className="px-6 py-2 ml-4 text-white hover:bg-orange-600 rounded transition-colors font-bold flex-shrink-0"
                  style={{ fontSize: '32px', lineHeight: '1' }}
                  title="閉じる"
                >
                  ×
                </button>
              </div>

              <div className="p-4">
                <div className="flex flex-col gap-2 mb-2">
                  {/* 2行目：ツールバー */}
                  <div className="flex justify-between items-center">
                    {/* 左側：描画ツール */}
                    <div className="flex items-center gap-2">
                      {/* サイズスライダー */}
                      <div className="flex items-center py-1 px-2 border-r border-[#b9b7b8] gap-2">
                        <div>
                          {renderBrushShape(16)}
                        </div>
                        <div>
                          <Slider
                            value={imageEditingBrushSize}
                            onChange={(e) => {
                              dispatch(imageEditingActions.setImageEditingBrushSize(e.value as number))
                            }}
                            min={10}
                            max={100}
                          />
                        </div>
                        <div>
                          {renderBrushShape(32)}
                        </div>
                      </div>

                      {/* Undo/Redo/クリア */}
                      <div className="flex items-center py-1 px-2 gap-2">
                        <button
                          onClick={handleUndo}
                          disabled={!canUndo}
                          className="p-1 hover:cursor-pointer disabled:opacity-50"
                          title="1つ前に戻る（モザイクを取り消し）"
                        >
                          <UndoIcon style={{
                            fill: canUndo ? '#595959' : '#d1d1d1',
                            width: '24px',
                            height: '24px'
                          }} />
                        </button>
                        <button
                          onClick={handleRedo}
                          disabled={!canRedo}
                          className="p-1 hover:cursor-pointer disabled:opacity-50"
                          title="1つ先に進める（モザイクをやり直し）"
                        >
                          <RedoIcon style={{
                            fill: canRedo ? '#595959' : '#d1d1d1',
                            width: '24px',
                            height: '24px'
                          }} />
                        </button>
                        <div className="w-[1px] h-[24px] bg-[#d7d7d7] mx-2"></div>
                        <button
                          onClick={handleClearAll}
                          disabled={!hasMosaicApplied}
                          className="px-3 py-1 font-medium disabled:text-[#d1d1d1] text-sm"
                          title="すべてのモザイクをクリア"
                        >
                          クリア
                        </button>
                      </div>
                    </div>

                    {/* 右側：確定ボタン */}
                    <div className="flex items-center">
                      <button
                        onClick={handleConfirm}
                        disabled={!canConfirm}
                        className={`px-4 py-1 font-medium rounded transition-all ${
                          canConfirm
                            ? 'bg-orange-500 text-white hover:bg-orange-600'
                            : 'bg-gray-200 text-gray-400 cursor-not-allowed opacity-60'
                        }`}
                        title={canConfirm ? 'モザイクを確定して保存' : 'モザイクを適用してから確定してください'}
                      >
                        確定
                      </button>
                    </div>
                  </div>
                </div>

                {/* 描画エリア */}
                <ImageEditingDrawing />
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default ImageEditingDrawingPopup
